import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OrderPrint = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const hasPrinted = useRef(false);

  useEffect(() => {
    fetchOrder();
  }, []);

  useEffect(() => {
    if (order && !hasPrinted.current) {
      hasPrinted.current = true;
      setTimeout(() => window.print(), 400);
    }
  }, [order]);

  const fetchOrder = async () => {
    try {
      const response = await axios.get(`${API}/orders/${orderId}/print-data`, { withCredentials: true });
      setOrder(response.data);
      // Mark as printed
      await axios.put(`${API}/orders/${orderId}/status?status=printed`, {}, { withCredentials: true });
    } catch {
      toast.error('Failed to load order');
    } finally {
      setLoading(false);
    }
  };

  if (loading || !order) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const formatDate = (d) => {
    if (!d) return '';
    try { return new Date(d).toLocaleDateString('en-ZA', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return d; }
  };

  return (
    <>
      <style>{`
        @media print {
          @page {
            size: 80mm auto;
            margin: 2mm;
          }
          html, body {
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
          }
          /* Hide sidebar, header, nav, and screen controls */
          .screen-controls,
          nav, aside, header, .no-print,
          [data-testid="sidebar"],
          [data-testid="mobile-menu-btn"] {
            display: none !important;
            width: 0 !important;
            height: 0 !important;
            overflow: hidden !important;
          }
          /* Keep all wrapper elements visible but strip their layout styling */
          #root, #root > *, main, .min-h-screen, .flex-1,
          .min-h-screen > *, main > * {
            display: block !important;
            margin: 0 !important;
            padding: 0 !important;
            border: none !important;
            box-shadow: none !important;
            width: 100% !important;
            min-height: 0 !important;
            overflow: visible !important;
            background: white !important;
          }
          .order-print-container {
            display: block !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          .order-docket {
            display: block !important;
          }
        }
        .order-docket {
          width: 80mm;
          font-family: 'Courier New', monospace;
          font-size: 10pt;
          color: black;
          background: white;
          padding: 2mm;
        }
        .order-docket .docket-header {
          text-align: center;
          margin-bottom: 2mm;
        }
        .order-docket .docket-title {
          font-size: 14pt;
          font-weight: 900;
        }
        .order-docket .docket-subtitle {
          font-size: 10pt;
          margin-top: 1mm;
        }
        .order-docket .docket-line {
          border-top: 1px dashed black;
          margin: 2mm 0;
        }
        .order-docket .info-row {
          display: flex;
          justify-content: space-between;
          font-size: 9pt;
          line-height: 1.5;
        }
        .order-docket .info-label {
          font-weight: 700;
        }
        .order-docket .items-table {
          width: 100%;
          font-size: 8.5pt;
          border-collapse: collapse;
        }
        .order-docket .items-table th {
          font-weight: 700;
          text-align: left;
          padding: 0.5mm 0;
          border-bottom: 1px solid black;
        }
        .order-docket .items-table th:not(:first-child),
        .order-docket .items-table td:not(:first-child) {
          text-align: right;
          width: 12mm;
        }
        .order-docket .items-table td {
          padding: 0.5mm 0;
          vertical-align: top;
        }
        .order-docket .total-row {
          display: flex;
          justify-content: space-between;
          font-weight: 900;
          font-size: 10pt;
        }
        .order-docket .footer {
          text-align: center;
          font-size: 8pt;
          margin-top: 3mm;
          color: #555;
        }
      `}</style>

      {/* Screen controls */}
      <div className="screen-controls p-6 space-y-4 max-w-2xl mx-auto">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">Order Docket Preview</h1>
          <div className="flex gap-2">
            <button onClick={() => navigate('/orders')}
              className="px-4 py-2 border rounded-md hover:bg-gray-50">
              Back
            </button>
            <button onClick={() => window.print()}
              className="px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800"
              data-testid="print-order-btn">
              Print Docket
            </button>
          </div>
        </div>
        <p className="text-sm text-gray-500">
          Select your receipt printer in the print dialog.
        </p>
      </div>

      {/* Docket */}
      <div className="order-print-container">
        <div className="screen-controls flex justify-center p-6">
          <div className="order-docket" style={{ border: '1px dashed #ccc' }}>
            <DocketContent order={order} formatDate={formatDate} />
          </div>
        </div>
        {/* Hidden print copy without border */}
        <div className="order-docket" style={{ display: 'none' }}>
          <DocketContent order={order} formatDate={formatDate} />
        </div>
      </div>
    </>
  );
};

const DocketContent = ({ order, formatDate }) => (
  <>
    <div className="docket-header">
      <div className="docket-title">THE MERCHANT COFFEE</div>
      <div className="docket-subtitle">ORDER SLIP</div>
    </div>
    <div className="docket-line" />
    <div className="info-row">
      <span className="info-label">Supplier:</span>
      <span>{order.supplier_name}</span>
    </div>
    <div className="info-row">
      <span className="info-label">Branch:</span>
      <span>{order.branch_name}</span>
    </div>
    <div className="info-row">
      <span className="info-label">Date:</span>
      <span>{formatDate(order.date)}</span>
    </div>
    <div className="info-row">
      <span className="info-label">Order:</span>
      <span>{order.order_id?.slice(-8)}</span>
    </div>
    <div className="docket-line" />
    <table className="items-table">
      <thead>
        <tr>
          <th>ITEM</th>
          <th>SOH</th>
          <th>PAR</th>
          <th>ORD</th>
        </tr>
      </thead>
      <tbody>
        {order.items?.map((item, idx) => (
          <tr key={idx}>
            <td>{item.name}{item.unit ? ` (${item.unit})` : ''}</td>
            <td>{item.soh}</td>
            <td>{item.par}</td>
            <td>{item.order}</td>
          </tr>
        ))}
      </tbody>
    </table>
    <div className="docket-line" />
    <div className="total-row">
      <span>TOTAL VALUE:</span>
      <span>R{order.total_value?.toFixed(2)}</span>
    </div>
    <div className="info-row">
      <span>ITEMS:</span>
      <span>{order.items?.length}</span>
    </div>
    <div className="docket-line" />
    <div className="footer">
      Please confirm order quantities<br />before dispatching.
    </div>
  </>
);

export default OrderPrint;
